import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { addNewClient, generateRegistrationNumber, fetchClients } from '../../lib/supabase';

const NewClientModal = ({ isOpen, onClose, onClientAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'school',
    grade_year: '',
    status: 'active',
    parent_contact: '',
    notes: '',
    registration_number: ''
  });

  const [, setIsGeneratingRegNumber] = useState(false);
  const [regNumberError, setRegNumberError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [existingClients, setExistingClients] = useState([]);

  const clientTypes = [
    { value: 'below_8th', label: 'Below 8th Grade' },
    { value: '8th_10th', label: '8th to 10th Grade' },
    { value: 'puc', label: 'PUC (11th & 12th)' },
    { value: 'college', label: 'College Student' },
    { value: 'professional', label: 'Professional' }
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load existing clients for registration number validation
  useEffect(() => {
    const loadExistingClients = async () => {
      try {
        const clients = await fetchClients();
        setExistingClients(clients);
      } catch (err) {
        console.error('Error loading clients:', err);
      }
    };
    
    if (isOpen) {
      loadExistingClients();
    }
  }, [isOpen]);

  // Generate registration number when modal opens
  useEffect(() => {
    if (isOpen) {
      generateRegNumber();
    }
  }, [isOpen]);

  const generateRegNumber = async () => {
    setIsGeneratingRegNumber(true);
    setRegNumberError('');
    try {
      const regNumber = await generateRegistrationNumber();
      setFormData(prev => ({
        ...prev,
        registration_number: regNumber
      }));
    } catch (err) {
      console.error('Error generating registration number:', err);
      setRegNumberError('Failed to generate registration number');
    } finally {
      setIsGeneratingRegNumber(false);
    }
  };

  // Validation functions
  const validateRegistrationNumber = (regNumber) => {
    if (!regNumber) {
      setRegNumberError('Registration number is required');
      return false;
    }
    
    if (regNumber.length !== 11) {
      setRegNumberError('Registration number must be exactly 11 digits');
      return false;
    }
    
    if (!/^\d{4}NCC\d{4}$/.test(regNumber)) {
      setRegNumberError('Invalid format. Should be XXXXNCCYYYY where X and Y are digits');
      return false;
    }
    
    // Check if registration number already exists
    const exists = existingClients.some(client => client.registration_number === regNumber);
    if (exists) {
      setRegNumberError('This registration number already exists');
      return false;
    }
    
    setRegNumberError('');
    return true;
  };

  const validateEmail = (email) => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    
    setEmailError('');
    return true;
  };

  const validatePhone = (phone) => {
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    
    // Remove all non-digit characters for validation
    const phoneDigits = phone.replace(/\D/g, '');
    
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      setPhoneError('Phone number should be between 10 and 15 digits');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate on change
    if (name === 'registration_number') {
      validateRegistrationNumber(value);
    } else if (name === 'email') {
      validateEmail(value);
    } else if (name === 'phone') {
      validatePhone(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const isRegValid = validateRegistrationNumber(formData.registration_number);
    const isEmailValid = validateEmail(formData.email);
    const isPhoneValid = validatePhone(formData.phone);
    
    if (!isRegValid || !isEmailValid || !isPhoneValid) {
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      const newClient = await addNewClient(formData);
      onClientAdded(newClient);
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        type: 'school',
        grade_year: '',
        status: 'active',
        parent_contact: '',
        notes: '',
        registration_number: ''
      });
    } catch (err) {
      console.error('Error adding client:', err);
      setError('Failed to add client. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-teal-600 px-4 py-3 sm:px-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-white">Add New Client</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {error && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Registration Number</label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="text"
                      name="registration_number"
                      value={formData.registration_number}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${regNumberError ? 'border-red-500' : 'border-gray-300'} px-3 py-2 focus:border-teal-500 focus:ring-teal-500`}
                      maxLength={11}
                    />
                    <button
                      type="button"
                      onClick={generateRegNumber}
                      className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                    >
                      Regenerate
                    </button>
                  </div>
                  {regNumberError && (
                    <p className="mt-1 text-sm text-red-500">{regNumberError}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Format: XXXXNCCYYYY (11 digits total)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md border ${emailError ? 'border-red-500' : 'border-gray-300'} px-3 py-2 focus:border-teal-500 focus:ring-teal-500`}
                    required
                  />
                  {emailError && (
                    <p className="mt-1 text-sm text-red-500">{emailError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md border ${phoneError ? 'border-red-500' : 'border-gray-300'} px-3 py-2 focus:border-teal-500 focus:ring-teal-500`}
                    required
                  />
                  {phoneError && (
                    <p className="mt-1 text-sm text-red-500">{phoneError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                    >
                    {clientTypes.map(type => (
                        <option key={type.value} value={type.value}>
                        {type.label}
                        </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {formData.type === 'below_8th' || formData.type === '8th_10th' || formData.type === 'puc' 
                      ? 'Grade' 
                      : formData.type === 'college' 
                        ? 'Year' 
                        : 'Designation'}
                  </label>
                  <input
                    type="text"
                    name="grade_year"
                    value={formData.grade_year}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                    placeholder={formData.type === 'below_8th' || formData.type === '8th_10th' || formData.type === 'puc' 
                      ? 'e.g., 8th, 9th, 10th' 
                      : formData.type === 'college' 
                        ? 'e.g., 1st Year, 2nd Year' 
                        : 'e.g., Manager, Developer'}
                  />
                </div>

                {(formData.type === 'below_8th' || formData.type === '8th_10th' || formData.type === 'puc') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Parent Contact</label>
                    <input
                      type="tel"
                      name="parent_contact"
                      value={formData.parent_contact}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Initial Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Adding...' : 'Add Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewClientModal;